import { useEffect, useRef, useState } from "react";
import { Users, GraduationCap, Star, Globe2, Award, BookOpen } from "lucide-react";

/**
 * Animated counter that eases a number from 0 → target once in view.
 * IntersectionObserver triggers; CSS-animation free, uses requestAnimationFrame.
 */
function AnimatedNumber({ value, duration = 1800 }) {
    const [display, setDisplay] = useState(0);
    const ref = useRef(null);
    const hasRun = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting && !hasRun.current) {
                        hasRun.current = true;
                        const start = performance.now();
                        const tick = (now) => {
                            const progress = Math.min(1, (now - start) / duration);
                            // easeOutCubic
                            const eased = 1 - Math.pow(1 - progress, 3);
                            setDisplay(Math.round(value * eased));
                            if (progress < 1) requestAnimationFrame(tick);
                        };
                        requestAnimationFrame(tick);
                    }
                });
            },
            { threshold: 0.35 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [value, duration]);

    return <span ref={ref}>{display.toLocaleString("en-IN")}</span>;
}

const STATS = [
    { icon: Users,         value: 50000, suffix: "+", label: "Students taught",    testid: "stat-students" },
    { icon: GraduationCap, value: 5000,  suffix: "+", label: "Verified tutors",    testid: "stat-tutors" },
    { icon: Award,         value: 12,    suffix: "+ yrs", label: "Of trusted service", testid: "stat-years" },
    { icon: Star,          value: 4.8,   suffix: " / 5", label: "Avg. parent rating", testid: "stat-rating", decimal: true },
    { icon: Globe2,        value: 27,    suffix: "+",     label: "Countries served",  testid: "stat-countries" },
    { icon: BookOpen,      value: 100,   suffix: "+",     label: "Subjects covered",  testid: "stat-subjects" },
];

export default function StatsCounter() {
    return (
        <section
            data-testid="home-stats-counter"
            className="relative py-16 sm:py-20 bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#1E3A8A] text-white overflow-hidden"
        >
            <div className="absolute inset-0 opacity-[0.08] pointer-events-none bg-grid" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[720px] h-[400px] bg-amber-400/10 blur-3xl rounded-full pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/25 text-[10px] uppercase tracking-[0.25em] font-bold text-amber-300 mb-4">
                        Built on Trust · Since 2013
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
                        A legacy of <span className="text-amber-400">learning outcomes</span> parents keep talking about.
                    </h2>
                    <p className="mt-3 text-blue-100/80 text-sm sm:text-base max-w-2xl mx-auto">
                        Real numbers, not marketing fluff. Every stat is verifiable from our tutor roster, student records and parent feedback.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
                    {STATS.map((s) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={s.testid}
                                data-testid={s.testid}
                                className="relative group bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-amber-400/30 rounded-2xl p-5 sm:p-6 text-center transition-all backdrop-blur-sm"
                            >
                                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-amber-400/15 border border-amber-400/30 text-amber-300 mb-3 group-hover:scale-110 transition-transform">
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white tabular-nums">
                                    {s.decimal ? (
                                        <span>{s.value.toFixed(1)}</span>
                                    ) : (
                                        <AnimatedNumber value={s.value} />
                                    )}
                                    <span className="text-amber-400 text-lg sm:text-xl ml-0.5">{s.suffix}</span>
                                </div>
                                <div className="text-[11px] sm:text-xs text-blue-100/70 uppercase tracking-wider mt-2 font-semibold">
                                    {s.label}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
